import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { LoaderPinwheel, Search, X } from "lucide-react";
import IngresoForm from "../../components/ui/IngresoForm";
import PageSizeSelect from "../../components/ui/PageSizeSelect";

const IngresosPage = () => {
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedMovimiento, setSelectedMovimiento] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL + "/stock/ingresos";
  const navigate = useNavigate();

  const fetchIngresos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMovimientos(res.data);
    } catch {
      toast.error("No se pudieron cargar los ingresos de almacén");
    } finally {
      setLoading(false);
    }
  };

  useEffect(
    () => {
      fetchIngresos();
    }, // eslint-disable-next-line
    [],
  );
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, movimientos]);

  const filteredMovimientos = movimientos.filter((m) => {
    const term = searchTerm.toLowerCase();
    return [
      m.articulo,
      m.ordenId?._id,
      m.proveedorId?.name,
      m.usuarioId?.name,
      m.tipo_movimiento,
      m.cantidad,
    ].some((v) =>
      String(v ?? "")
        .toLowerCase()
        .includes(term),
    );
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredMovimientos.length / itemsPerPage),
  );
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMovimientos = filteredMovimientos.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Ingresos de Almacén
          </h2>
          <p className="text-sm text-gray-500">
            {movimientos.length} registro{movimientos.length !== 1 ? "s" : ""}
          </p>
        </div>
        <Button
          onClick={() => {
            setSelectedMovimiento(null);
            setShowModal(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          + Nuevo ingreso
        </Button>
      </div>

      {loading && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
          <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
          Cargando datos, por favor espera...
        </div>
      )}

      {/* Buscador */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por artículo, orden, proveedor o usuario…"
          className="w-full sm:w-96 pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Tabla */}
      <div className="flex items-center justify-end mb-2">
        <PageSizeSelect
          value={itemsPerPage}
          onChange={(size) => {
            setItemsPerPage(size);
            setCurrentPage(1);
          }}
        />
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wide">
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Artículo</th>
              <th className="px-4 py-3 text-left">Orden</th>
              <th className="px-4 py-3 text-left">Proveedor</th>
              <th className="px-4 py-3 text-left">Usuario</th>
              <th className="px-4 py-3 text-left">Tipo</th>
              <th className="px-4 py-3 text-center">Cantidad</th>
              <th className="px-4 py-3 text-left">Fecha</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {paginatedMovimientos.length > 0 ? (
              paginatedMovimientos.map((m, idx) => (
                <tr key={m._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {startIndex + idx + 1}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {m.articulo || "—"}
                  </td>
                  <td
                    className="px-4 py-3 text-blue-600 cursor-pointer hover:underline font-mono text-xs"
                    onClick={() => navigate(`/detalleOC/${m.ordenId?._id}`)}
                  >
                    {m.ordenId?._id ? m.ordenId._id.slice(-8) : "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {m.proveedorId?.name || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {m.usuarioId?.name || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        m.tipo_movimiento === "Ingreso"
                          ? "bg-green-50 text-green-700"
                          : "bg-red-50 text-red-700"
                      }`}
                    >
                      {m.tipo_movimiento}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-medium">
                    {m.cantidad}
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {new Date(m.fecha).toLocaleDateString()}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="py-12 text-center">
                  <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">
                    No hay ingresos para la búsqueda actual
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
        <span className="text-xs text-gray-400">
          {filteredMovimientos.length === 0
            ? "0"
            : `${startIndex + 1}–${Math.min(startIndex + itemsPerPage, filteredMovimientos.length)}`}{" "}
          de {filteredMovimientos.length}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ← Anterior
          </Button>
          <span className="px-2 py-1 bg-gray-100 rounded-md text-xs">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Siguiente →
          </Button>
        </div>
      </div>

      {showModal && (
        <IngresoForm
          movimiento={selectedMovimiento}
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false);
            fetchIngresos();
          }}
        />
      )}
    </div>
  );
};

export default IngresosPage;
